import type { JobWorkChallan, JobWorkChallanParty } from '@/types/production';

/**
 * Renders a GST Rule-55 "General Issue Challan" (job-work delivery challan) into
 * a standalone print window and triggers the browser print dialog. Kept as a
 * plain HTML document (not React) so it prints cleanly without app chrome/CSS.
 */

const esc = (v: unknown): string =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const money = (n: number): string =>
  Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const qtyFmt = (n: number): string =>
  Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const fmtDate = (v: string | null): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function party(p: JobWorkChallanParty): string {
  const addr = [p.line1, p.line2].filter(Boolean).join(', ');
  const cityLine = [p.city, p.state, p.postalCode].filter(Boolean).join(', ');
  return `
    <div class="party-name">${esc(p.name)}</div>
    ${addr ? `<div>${esc(addr)}</div>` : ''}
    ${cityLine ? `<div>${esc(cityLine)}${p.country ? ', ' + esc(p.country) : ''}</div>` : ''}
    ${p.phone ? `<div>Ph: ${esc(p.phone)}</div>` : ''}
    <div class="party-gst">
      <span>State Code: <b>${esc(p.stateCode || '—')}</b></span>
      <span>GSTIN: <b>${esc(p.gstin || '—')}</b></span>
    </div>`;
}

export function buildChallanHtml(c: JobWorkChallan): string {
  const inter = c.interState;
  const taxCols = inter
    ? `<th class="num">IGST%</th><th class="num">IGST Amt</th>`
    : `<th class="num">CGST%</th><th class="num">CGST Amt</th><th class="num">SGST%</th><th class="num">SGST Amt</th>`;

  const rows = c.lines
    .map((l) => {
      const taxCells = inter
        ? `<td class="num">${l.igstRate}</td><td class="num">${money(l.igstAmt)}</td>`
        : `<td class="num">${l.cgstRate}</td><td class="num">${money(l.cgstAmt)}</td>` +
          `<td class="num">${l.sgstRate}</td><td class="num">${money(l.sgstAmt)}</td>`;
      return `<tr>
        <td class="num">${l.sno}</td>
        <td>${esc(l.jobNo)}</td>
        <td>${esc(l.description)}${l.sizeColourStyle ? `<div class="sub">${esc(l.sizeColourStyle)}</div>` : ''}</td>
        <td>${esc(l.productDescription)}</td>
        <td>${esc(l.hsn)}</td>
        <td>${esc(l.uom)}</td>
        <td class="num">${qtyFmt(l.qty)}</td>
        <td class="num">${money(l.rate)}</td>
        <td class="num">${money(l.total)}</td>
        ${taxCells}
      </tr>`;
    })
    .join('');

  const taxSummary = inter
    ? `<tr><td>IGST</td><td class="num">${money(c.tax.igstAmt)}</td></tr>`
    : `<tr><td>CGST</td><td class="num">${money(c.tax.cgstAmt)}</td></tr>
       <tr><td>SGST</td><td class="num">${money(c.tax.sgstAmt)}</td></tr>`;

  const g = c.general;

  return `<!doctype html><html><head><meta charset="utf-8" />
  <title>${esc(c.challan.number)} — General Issue Challan</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111; margin: 0; padding: 18px 22px; font-size: 11px; }
    .doc-title { text-align: center; margin-bottom: 10px; }
    .doc-title .company { font-size: 18px; font-weight: 700; }
    .doc-title .title { font-size: 13px; font-weight: 600; letter-spacing: .5px; margin-top: 2px; text-transform: uppercase; }
    .parties { display: flex; border: 1px solid #444; }
    .parties > div { flex: 1; padding: 8px 10px; line-height: 1.45; }
    .parties > div:first-child { border-right: 1px solid #444; }
    .party-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #555; margin-bottom: 3px; }
    .party-name { font-weight: 700; font-size: 12px; }
    .party-gst { margin-top: 4px; display: flex; gap: 14px; flex-wrap: wrap; }
    .meta { display: flex; border: 1px solid #444; border-top: none; }
    .meta > div { flex: 1; padding: 8px 10px; }
    .meta > div:first-child { border-right: 1px solid #444; }
    .meta-row { display: flex; justify-content: space-between; gap: 10px; padding: 1.5px 0; }
    .meta-row span:first-child { color: #555; }
    .meta-row b { font-weight: 600; }
    table.items { width: 100%; border-collapse: collapse; margin-top: 10px; }
    table.items th, table.items td { border: 1px solid #444; padding: 4px 5px; text-align: left; vertical-align: top; }
    table.items th { background: #f0f0f0; font-size: 9px; text-transform: uppercase; letter-spacing: .3px; }
    table.items td.num, table.items th.num { text-align: right; font-variant-numeric: tabular-nums; }
    table.items .sub { color: #666; font-size: 9.5px; margin-top: 1px; }
    .totals { display: flex; justify-content: space-between; margin-top: 10px; gap: 16px; }
    .words { flex: 1; padding: 8px 10px; border: 1px solid #444; }
    .words .lbl { font-size: 9px; text-transform: uppercase; color: #555; }
    .words .val { font-weight: 600; margin-top: 2px; }
    table.tax { border-collapse: collapse; min-width: 230px; }
    table.tax td { border: 1px solid #444; padding: 4px 10px; }
    table.tax td.num { text-align: right; font-variant-numeric: tabular-nums; }
    table.tax tr.grand td { font-weight: 700; background: #f0f0f0; }
    .note { margin-top: 12px; padding: 6px 10px; border: 1px dashed #888; font-weight: 600; text-align: center; font-size: 10.5px; }
    .signs { display: flex; justify-content: space-between; margin-top: 34px; }
    .signs > div { text-align: center; font-size: 10px; color: #555; border-top: 1px solid #888; padding-top: 4px; width: 30%; }
    @media print { body { padding: 0; } @page { margin: 12mm; } }
  </style></head><body>
    <div class="doc-title">
      <div class="company">${esc(c.company)}</div>
      <div class="title">${esc(c.title)}</div>
    </div>

    <div class="parties">
      <div>
        <div class="party-label">Consigner (From)</div>
        ${party(c.consigner)}
      </div>
      <div>
        <div class="party-label">Consignee (Job Worker)</div>
        ${party(c.consignee)}
      </div>
    </div>

    <div class="meta">
      <div>
        <div class="meta-row"><span>Activity</span><b>${esc(g.activity)}</b></div>
        <div class="meta-row"><span>Returnable Type</span><b>${esc(g.returnableType)}</b></div>
        <div class="meta-row"><span>E-Way Bill No</span><b>${esc(g.ewayBillNo || '—')}</b></div>
        <div class="meta-row"><span>E-Way Bill Date</span><b>${fmtDate(g.ewayBillDate)}</b></div>
      </div>
      <div>
        <div class="meta-row"><span>Challan No</span><b>${esc(c.challan.number)}</b></div>
        <div class="meta-row"><span>Challan Date</span><b>${fmtDate(c.challan.date)}</b></div>
        <div class="meta-row"><span>Vehicle No</span><b>${esc(g.vehicleNo || '—')}</b></div>
        <div class="meta-row"><span>Service PO No</span><b>${esc(g.servicePoNo || '—')}</b></div>
        <div class="meta-row"><span>Return Date</span><b>${fmtDate(g.returnDate)}</b></div>
      </div>
    </div>

    <table class="items">
      <thead><tr>
        <th>S.No</th><th>Job No</th><th>Description of Goods</th><th>Product</th>
        <th>HSN</th><th>UOM</th><th class="num">Qty</th><th class="num">Rate</th><th class="num">Total</th>
        ${taxCols}
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>

    <div class="totals">
      <div class="words">
        <div class="lbl">Amount in words</div>
        <div class="val">${esc(c.amountInWords)}</div>
        <div class="lbl" style="margin-top:10px;">Remarks</div>
        <div class="val" style="font-weight:400;">—</div>
      </div>
      <table class="tax">
        <tr><td>Taxable Value</td><td class="num">${money(c.tax.taxableTotal)}</td></tr>
        ${taxSummary}
        <tr><td>Total Tax</td><td class="num">${money(c.tax.totalTax)}</td></tr>
        <tr class="grand"><td>Grand Total</td><td class="num">₹ ${money(c.tax.grandTotal)}</td></tr>
      </table>
    </div>

    <div class="note">${esc(c.footer)}</div>

    <div class="signs">
      <div>Prepared By</div>
      <div>Approved By</div>
      <div>Consignee Signature &amp; Seal</div>
    </div>
  </body></html>`;
}

export function printJobWorkChallan(c: JobWorkChallan): void {
  const html = buildChallanHtml(c);
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) {
    throw new Error('Pop-up blocked — allow pop-ups to print the challan.');
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      w.print();
    } catch {
      /* window closed before print */
    }
  };
  w.onload = doPrint;
  // Fallback for browsers that fire load synchronously on document.write.
  setTimeout(doPrint, 500);
}
